/* ProposalsPage — Milestone 2: list client proposals per organization.
 * Shows funnel analytics cards, a status filter, and a table with actions.
 * "New proposal" + "Edit" route into the ProposalBuilderPage. */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  FileSignature, Plus, Loader2, Copy, Trash2, Pencil, Building2, TrendingUp, LayoutTemplate,
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const STATUS_STYLES = {
  draft: 'bg-zinc-100 text-zinc-600',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  expired: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-600 text-white',
};

const money = (cents, currency = 'usd') => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100);
  } catch { return `$${((cents || 0) / 100).toFixed(2)}`; }
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }); } catch { return iso; } };

const ProposalsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(false);

  const loadOrgs = useCallback(async () => {
    try {
      const r = await api.get('/orgs');
      const list = Array.isArray(r.data) ? r.data : (r.data.organizations || r.data.orgs || []);
      setOrgs(list);
      if (list.length && !orgId) setOrgId(list[0].id);
      if (!list.length) setLoading(false);
    } catch { setLoading(false); }
  }, [api, orgId]);

  const loadProposals = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = { organization_id: orgId };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [pr, an] = await Promise.all([
        api.get('/proposals', { params }),
        api.get('/proposals-analytics', { params: { organization_id: orgId } }),
      ]);
      setProposals(pr.data.proposals || []);
      setAnalytics(an.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load proposals'); }
    finally { setLoading(false); }
  }, [api, orgId, statusFilter]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);
  useEffect(() => { loadProposals(); }, [loadProposals]);

  const createNew = () => {
    if (!orgId) { toast.error('Create an organization first'); return; }
    navigate(`/proposals/new?org=${orgId}`);
  };

  const openTemplates = async () => {
    if (!orgId) { toast.error('Create an organization first'); return; }
    setTplOpen(true);
    setLoadingTpls(true);
    try {
      const r = await api.get('/proposal-templates', { params: { organization_id: orgId } });
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load templates'); }
    finally { setLoadingTpls(false); }
  };

  const applyTemplate = (t) => {
    setTplOpen(false);
    navigate(`/proposals/new?org=${orgId}&template=${t.id}`);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/proposal-templates/${t.id}`); toast.success('Template deleted'); setTemplates((prev) => prev.filter((x) => x.id !== t.id)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const duplicate = async (p) => {
    try { await api.post(`/proposals/${p.id}/duplicate`); toast.success('Proposal duplicated'); loadProposals(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not duplicate'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.number}? This cannot be undone.`)) return;
    try { await api.delete(`/proposals/${p.id}`); toast.success('Proposal deleted'); loadProposals(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const cards = analytics ? [
    { label: 'Pipeline (open)', value: money(analytics.pipeline_value_cents), key: 'pipeline' },
    { label: 'Win rate', value: `${analytics.win_rate || 0}%`, key: 'win' },
    { label: 'Avg deal size', value: money(analytics.avg_deal_size_cents), key: 'avg' },
    { label: 'Accepted value', value: money(analytics.accepted_value_cents), key: 'acc' },
    { label: 'Paid value', value: money(analytics.paid_value_cents), key: 'paid' },
  ] : [];

  const FUNNEL_COLORS = {
    draft: '#a1a1aa', sent: '#3b82f6', viewed: '#6366f1',
    accepted: '#10b981', declined: '#f43f5e', expired: '#f59e0b', paid: '#059669',
  };
  const funnelData = analytics ? ['draft', 'sent', 'viewed', 'accepted', 'declined', 'paid']
    .map((s) => ({ name: s.charAt(0).toUpperCase() + s.slice(1), key: s, count: analytics.counts?.[s] || 0 })) : [];

  return (
    <div className="space-y-6" data-testid="proposals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-indigo-600" />
            Proposals
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Draft branded client proposals with line items and server-computed totals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openTemplates} data-testid="new-from-template-btn">
            <LayoutTemplate className="w-4 h-4 mr-2" /> Templates
          </Button>
          <Button onClick={createNew} data-testid="new-proposal-btn">
            <Plus className="w-4 h-4 mr-2" /> New proposal
          </Button>
        </div>
      </div>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-indigo-600" /> Start from a template</DialogTitle>
            <DialogDescription>Reuse your saved proposal templates to create a new draft in seconds.</DialogDescription>
          </DialogHeader>
          {loadingTpls ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center" data-testid="templates-empty">No templates yet. Open a draft and click “Save as template” to create one.</p>
          ) : (
            <div className="divide-y divide-zinc-100 max-h-80 overflow-y-auto">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`template-row-${t.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{t.name}</p>
                    <p className="text-xs text-zinc-500">{(t.line_items || []).length} line item(s) · {(t.currency || 'usd').toUpperCase()}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" onClick={() => applyTemplate(t)} data-testid={`use-template-${t.id}`}>Use</Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteTemplate(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Org + status filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-zinc-400" />
          <Select value={orgId || ''} onValueChange={setOrgId}>
            <SelectTrigger className="w-56" data-testid="proposals-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="proposals-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {['all', 'draft', 'sent', 'viewed', 'accepted', 'declined', 'expired', 'paid'].map((s) => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Analytics dashboard */}
      {analytics && (
        <div className="space-y-4" data-testid="proposals-analytics">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {cards.map((c) => (
              <Card key={c.key}><CardContent className="p-4">
                <p className="text-xs text-zinc-500">{c.label}</p>
                <p className="text-2xl font-bold mt-1">{c.value}</p>
              </CardContent></Card>
            ))}
          </div>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-600" /> Funnel</p>
                <p className="text-xs text-zinc-500">View rate {analytics.view_rate || 0}% · {analytics.total} total · {analytics.won_count || 0} won</p>
              </div>
              <div style={{ width: '100%', height: 200 }} data-testid="proposals-funnel-chart">
                <ResponsiveContainer>
                  <BarChart data={funnelData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {funnelData.map((d) => <Cell key={d.key} fill={FUNNEL_COLORS[d.key]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
          ) : !orgs.length ? (
            <p className="text-sm text-zinc-500 p-8 text-center">Create an organization to start building proposals.</p>
          ) : proposals.length === 0 ? (
            <div className="text-center py-12" data-testid="proposals-empty">
              <FileSignature className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">No proposals yet. Click “New proposal” to create one.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {proposals.map((p) => (
                <div key={p.id} className="flex items-center gap-4 px-4 py-3 hover:bg-zinc-50" data-testid={`proposal-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-400">{p.number}</span>
                      <span className="font-medium text-zinc-900 truncate">{p.title || 'Untitled Proposal'}</span>
                      <Badge className={STATUS_STYLES[p.status] || 'bg-zinc-100 text-zinc-600'}>{p.status}</Badge>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5 truncate">
                      {p.prospect?.name || p.prospect?.company || 'No client'} · {fmtDate(p.created_at)}
                    </p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-semibold">{money(p.totals?.one_time_total_cents, p.currency)}</p>
                    {p.totals?.monthly_total_cents ? <p className="text-xs text-zinc-500">+{money(p.totals.monthly_total_cents, p.currency)}/mo</p> : null}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/proposals/${p.id}`)} data-testid={`edit-proposal-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => duplicate(p)} data-testid={`duplicate-proposal-${p.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => remove(p)} data-testid={`delete-proposal-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProposalsPage;
export { ProposalsPage };
